/**
 * 多文件分享组 API
 */

import { UploadMetadata, uploadFileWithMetadata } from './upload'
import { API_BASE_URL, API_ROOT_URL } from '@/constants/api'

export interface CreateShareGroupParams {
  fileIds: string[]             // 文件ID列表
  shareName?: string            // 分享名称（可选，默认取第一个文件名）
  expireDays?: number          // 有效期（天）
  password?: string            // 分享密码（可选）
  burnAfterRead?: boolean      // 下载后销毁
  maxDownloads?: number        // 最大下载次数（可选）
  metadata?: UploadMetadata    // 额外参数
}

export interface ShareGroupResponse {
  code: number
  data: {
    shareKey: string
    downloadUrl: string
    fileCount: number
    totalSize: number
    expireAt: string
    metadata?: UploadMetadata
  }
}

export interface ShareGroupInfo {
  code: number
  data: {
    shareName: string
    fileCount: number
    totalSize: number
    downloadCount: number
    maxDownloads: number
    expireAt: string
    isExpired: boolean
    passwordProtected: boolean
    burnAfterRead: boolean
  }
}

export async function createShareGroup(params: CreateShareGroupParams): Promise<ShareGroupResponse> {
  const response = await fetch(`${API_BASE_URL}/share-group/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  })
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to create share group')
  }
  
  return response.json()
}

export async function getShareGroupInfo(shareKey: string): Promise<ShareGroupInfo> {
  const response = await fetch(`${API_BASE_URL}/share-group/${shareKey}/info`)
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to get share group info')
  }
  
  return response.json()
}

export async function verifyShareGroupDownload(shareKey: string, password?: string): Promise<{ success: boolean; message?: string; reason?: string }> {
  const formData = new FormData()
  if (password) {
    formData.append('password', password)
  }

  const response = await fetch(`${API_ROOT_URL}/download/group/${shareKey}/verify`, {
    method: 'POST',
    body: formData,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    return {
      success: false,
      message: error.message || '验证失败',
      reason: error.reason,
    }
  }

  return { success: true }
}

export function triggerNativeShareGroupDownload(shareKey: string, password?: string): void {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = `${API_ROOT_URL}/download/group/${shareKey}`
  form.style.display = 'none'

  if (password) {
    const pwdInput = document.createElement('input')
    pwdInput.type = 'hidden'
    pwdInput.name = 'password'
    pwdInput.value = password
    form.appendChild(pwdInput)
  }

  document.body.appendChild(form)
  form.submit()
  document.body.removeChild(form)
}

export async function downloadShareGroup(shareKey: string, password?: string): Promise<Blob> {
  const formData = new FormData()
  if (password) {
    formData.append('password', password)
  }
  
  const response = await fetch(`${API_ROOT_URL}/download/group/${shareKey}`, {
    method: 'POST',
    body: formData,
  })
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to download share group')
  }
  
  return response.blob()
}

export async function uploadAndShareMultipleFiles(
  files: File[],
  options: {
    shareName?: string
    deviceId?: string
    expireDays?: number
    password?: string
    burnAfterRead?: boolean
    encrypted?: boolean
    allowDownload?: boolean
    allowClone?: boolean
    allowTransfer?: boolean
    description?: string
    onProgress?: (progress: number) => void
    onFileProgress?: (fileIndex: number, fileName: string, progress: number) => void
    onSpeedUpdate?: (fileIndex: number, fileName: string, speed: string, remaining: string) => void
    onShareInfoReady?: (shareKey: string, downloadUrl: string) => void
    onPhaseChange?: (phase: 'hashing' | 'uploading' | 'completing') => void
  }
): Promise<ShareGroupResponse> {
  const fileIds: string[] = []
  const totalFiles = files.length
  let shareGroupResult: ShareGroupResponse | null = null
  let sharedShareId: number | undefined
  let sharedShareKey: string | undefined
  let sharedDownloadUrl: string | undefined
  
  try {
    for (let i = 0; i < totalFiles; i++) {
      const file = files[i]
      const isFirstFile = i === 0
      
      const uploadResult = await uploadFileWithMetadata(file, {
        shareId: sharedShareId,
        deviceId: options.deviceId,
        expireDays: isFirstFile ? (options.expireDays || 7) : undefined,
        password: isFirstFile ? options.password : undefined,
        burnAfterRead: isFirstFile ? (options.burnAfterRead || false) : undefined,
        encrypted: options.encrypted,
        allowDownload: options.allowDownload,
        allowClone: options.allowClone,
        allowTransfer: options.allowTransfer,
        description: options.description,
        onPhaseChange: options.onPhaseChange,
        onProgress: (fileProgress) => {
          if (options.onFileProgress) {
            options.onFileProgress(i, file.name, fileProgress)
          }
          
          if (options.onProgress) {
            const baseProgress = (i / totalFiles) * 100
            const currentFileProgress = (fileProgress / 100) * (100 / totalFiles)
            const totalProgress = Math.round(baseProgress + currentFileProgress)
            options.onProgress(totalProgress)
          }
        },
        onSpeedUpdate: (stats) => {
          if (options.onSpeedUpdate) {
            options.onSpeedUpdate(i, file.name, stats.speedFormatted, stats.remainingFormatted)
          }
        },
        onShareInfoReady: (shareKey, downloadUrl) => {
          if (isFirstFile && options.onShareInfoReady) {
            sharedShareKey = shareKey
            sharedDownloadUrl = downloadUrl
            options.onShareInfoReady(shareKey, downloadUrl)
          }
        }
      })
      
      fileIds.push(uploadResult.data.fileId)
      
      if (isFirstFile) {
        if (uploadResult.data.shareId) {
          sharedShareId = typeof uploadResult.data.shareId === 'number' 
            ? uploadResult.data.shareId 
            : Number(uploadResult.data.shareId)
        }
        if (uploadResult.data.shareKey) {
          sharedShareKey = uploadResult.data.shareKey
        }
        if (uploadResult.data.downloadUrl) {
          sharedDownloadUrl = uploadResult.data.downloadUrl
        }
      }
    }
    
    if (options.onProgress) options.onProgress(100)
    
    shareGroupResult = {
      code: 200,
      data: {
        shareKey: sharedShareKey || '',
        downloadUrl: sharedDownloadUrl || '',
        fileCount: totalFiles,
        totalSize: files.reduce((sum, f) => sum + f.size, 0),
        expireAt: '',
      }
    }
    
    return shareGroupResult
  } catch (error) {
    console.error('Multi-file upload failed:', error)
    throw error
  }
}
